import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Typography, IconButton, Menu, MenuItem, Chip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PageLayout from '@shared/shell/Layout/PageLayout';
import { useWarehouse } from '../../hooks/useWarehouse';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import DialogContainer from '@shared/ui/dialog/DialogContainer';
import WarehouseInventoryCount from '../warehouse-inventory/WarehouseInventoryCount';
import WarehouseInventory from '../warehouse-inventory/WarehouseInventory';
import WarehouseConnections from '../warehouse-detail/WarehouseConnections';
import { DeleteWarehouseDialog } from './DeleteWarehouseDialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { useToast } from '@shared/hooks/useToast';
import { APP_CONFIG } from '../../../../config/app.config';

const WarehouseDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { warehouse } = useWarehouse(id!);
  const [isCountDialogOpen, setIsCountDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const { mutateAsync: deleteWarehouse } = useMutation({
    mutationFn: async (options: { deleteConnections: boolean; deleteMutations: boolean }) => {
      if (!id) throw new Error('No warehouse ID provided');
      const result = await container.warehouseRepository.deleteWarehouse(id, options);
      if (result.status === 'error') throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['warehouses']);
      toast.success('Warehouse deleted successfully');
      navigate('/inventory/warehouses');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });

  const handleDeleteConfirm = (options: { deleteConnections: boolean; deleteMutations: boolean }) => {
    deleteWarehouse(options);
  };

  return (
    <PageLayout title={warehouse?.name || 'Warehouse Details'} backAction={() => navigate('/inventory/warehouses')}>
      <Box sx={{ display: 'flex', gap: 4 }}>
        <Box sx={{ minWidth: '400px', flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <DialogContainer>
            <DialogHeader>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <Typography variant="body1">Warehouse Details</Typography>
                <IconButton onClick={handleMenuOpen}>
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  <MenuItem 
                    onClick={handleDeleteClick}
                    sx={{
                      color: APP_CONFIG.ui.theme.colors.error,
                      '&:hover': {
                        backgroundColor: APP_CONFIG.ui.theme.colors.error + '10',
                      }
                    }}
                  >
                    Delete Warehouse
                  </MenuItem>
                </Menu>
              </Box>
            </DialogHeader>

            <Box px={3} py={2} sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
              <Typography variant="body1">Name</Typography>
              <Typography variant="body1">{warehouse?.name}</Typography>
            </Box>

            <Box px={3} py={2} sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
              <Typography variant="body1">Location</Typography>
              <Typography variant="body1">{warehouse?.location || 'N/A'}</Typography>
            </Box>

            <Box px={3} py={2} sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
              <Typography variant="body1">Status</Typography>
              <Chip 
                label={warehouse?.active ? 'Active' : 'Inactive'} 
                color={warehouse?.active ? 'success' : 'default'} 
                size="small" 
              />
            </Box>
          </DialogContainer>

          <WarehouseConnections />
        </Box>

        <WarehouseInventory warehouseId={id!} />

        <WarehouseInventoryCount
          warehouseId={id!}
          onClose={() => setIsCountDialogOpen(false)}
          onSubmit={() => setIsCountDialogOpen(false)}
          open={isCountDialogOpen}
        />

        <DeleteWarehouseDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          warehouseName={warehouse?.name || ''}
        />
      </Box>
    </PageLayout>
  );
};

export default WarehouseDetail; 